import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  DownloadToPstFileComponent,
  DownloadToPstFileModalData
} from '@common/components/modals/download-to-pst-file/download-to-pst-file.component';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { formatWithSpaces } from '@common/utils';
import { MbsSize, ModalService } from 'mbs-ui-kit';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ExportToPstHelper {
  private readonly ability = inject(AbilityService);
  private readonly authContext = inject(AuthContextService);
  private readonly modalService = inject(ModalService);

  private errorRoleText = 'Caught error manually: Forbidden role';

  protected readonly authUserId = this.authContext.id;
  protected readonly authDomainId = this.authContext.domainId;

  protected readonly isAdmin = this.ability.isAdmin;
  protected readonly isLimitedAdmin = this.ability.isLimitedAdmin;
  protected readonly isSingleUser = this.ability.isSingleUser;
  protected readonly isDomainUser = this.ability.isDomainUser;

  catchErrorDownloadFile(error: HttpErrorResponse, func: () => Observable<any>): Observable<never | any> {
    // 513 - error means that don't selected checkbox 'Direct PST Download' in My Account -> General Tab
    if (error.status === 513) {
      const data: DownloadToPstFileModalData = {
        id: this.getExportOwnerId(),
        taskHasStarted: false,
        countRequired: formatWithSpaces(error?.error?.minItemsCountRequiredPasswordProtection || 9999)
      };

      return this.handleCatchError(data, func);
    }

    return throwError(() => error);
  }

  private getExportOwnerId(): string {
    if (this.isAdmin() || this.isLimitedAdmin()) return this.authDomainId();
    if (this.isSingleUser() || this.isDomainUser()) return this.authUserId();

    throw new Error(this.errorRoleText);
  }

  private handleCatchError(data: DownloadToPstFileModalData, func: () => Observable<any>): Observable<any> {
    const startedData: DownloadToPstFileModalData = { ...data, taskHasStarted: true, func };

    return from(this.modalService.openCustom(DownloadToPstFileComponent, { size: MbsSize.sm, data })).pipe(
      switchMap(() => this.modalService.openCustom(DownloadToPstFileComponent, { size: MbsSize.sm, data: startedData })),
      catchError((err) => throwError(() => err))
    );
  }
}
